"use client";

import React from "react";
import { ArrowRight, Flame, CircleAlert as AlertCircle, Droplet, ShieldAlert, Wrench, PhoneCall, Search } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface PlumbingServiceItem {
  id: string;
  title: string;
  description: string;
  iconName: "flame" | "droplet" | "alert" | "shield" | "wrench" | "search";
  badge: string;
  linkTo: string;
  actionText: string;
}
const servicesData: PlumbingServiceItem[] = [{
  id: "water-heaters",
  title: "Water Heater Repair & Installation",
  description: "Rapid diagnosis and repair for leaking, lukewarm, or failed tank and tankless water heaters across Jonesboro.",
  iconName: "flame",
  badge: "24/7 Priority",
  linkTo: "/water-heater/",
  actionText: "Explore Water Heater Service"
}, {
  id: "clogged-drains",
  title: "Severe Clog Removal & Drain Cleaning",
  description: "High-power hydro jetting and professional snake snarl clearing for stubborn backup issues in sinks, tubs, and main lines.",
  iconName: "alert",
  badge: "Same-Day Dispatch",
  linkTo: "/problems/",
  actionText: "Fix Drain Problems"
}, {
  id: "burst-pipes",
  title: "Burst Pipe & Emergency Leak Repair",
  description: "Immediate shut-off assistance, leak pinpointing, and durable pipe replacements to prevent structural water damage.",
  iconName: "droplet",
  badge: "Urgent Response",
  linkTo: "/services/",
  actionText: "View Leak Solutions"
}, {
  id: "sewer-backup",
  title: "Sewer Line Inspection & Repair",
  description: "High-definition video camera pipe diagnostics to locate root intrusions, cracked joints, and major sewer blockages.",
  iconName: "search",
  badge: "Camera Diagnostics",
  linkTo: "/services/",
  actionText: "Learn About Sewer Care"
}, {
  id: "full-plumbing",
  title: "Comprehensive Plumbing Services",
  description: "Complete residential and light commercial fixture installations, garbage disposals, toilet rebuilds, and valve replacements.",
  iconName: "wrench",
  badge: "Licensed Techs",
  linkTo: "/services/",
  actionText: "Browse All Services"
}, {
  id: "local-areas",
  title: "Jonesboro & Clayton County Service Area",
  description: "Local plumbing crews positioned for quick travel times throughout Jonesboro and neighboring Clayton County communities.",
  iconName: "shield",
  badge: "Local Fast Arrival",
  linkTo: "/service-areas/",
  actionText: "Check Service Coverage"
}];
function ServiceIcon({
  name
}: {
  name: PlumbingServiceItem["iconName"];
}) {
  const iconProps = {
    className: "size-6 text-primary",
    "aria-hidden": true
  };
  switch (name) {
    case "flame":
      return <Flame {...iconProps} />;
    case "droplet":
      return <Droplet {...iconProps} />;
    case "alert":
      return <AlertCircle {...iconProps} />;
    case "shield":
      return <ShieldAlert {...iconProps} />;
    case "wrench":
      return <Wrench {...iconProps} />;
    case "search":
      return <Search {...iconProps} />;
    default:
      return <Wrench {...iconProps} />;
  }
}
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="relatedservices" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-3 text-xs uppercase tracking-wider font-semibold border-primary/20 text-primary bg-secondary/50">
            More Plumbing Solutions
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Related Plumbing Services in Jonesboro
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From sudden midnight pipe bursts to scheduled water heater replacements, our certified Jonesboro technicians handle every plumbing challenge promptly.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {servicesData.map((service, index) => <motion.div key={service.id} data-index={index} variants={cardVariants} className="h-full flex flex-col">
              <Card className="h-full flex flex-col justify-between border border-border/80 bg-card hover:border-primary/40 hover:shadow-md transition-[box-shadow,border-color] duration-300 rounded-xl">
                <div>
                  <CardHeader className="space-y-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="p-3 rounded-lg bg-secondary text-secondary-foreground inline-flex items-center justify-center">
                        <ServiceIcon name={service.iconName} />
                      </div>
                      <Badge variant="secondary" className="text-xs font-medium bg-muted text-muted-foreground">
                        {service.badge}
                      </Badge>
                    </div>
                    <CardTitle className="font-default text-xl font-bold text-card-foreground">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <CardFooter className="pt-4 border-t border-border/40">
                  <Button asChild variant="ghost" className="p-0 h-auto font-semibold text-primary hover:text-primary/80 hover:bg-transparent group inline-flex items-center gap-2">
                    <Link to={service.linkTo} className="inline-flex items-center gap-1.5">
                      <span>{service.actionText}</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-14 rounded-2xl bg-secondary/60 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="font-default text-lg sm:text-xl font-bold text-secondary-foreground">
              Need immediate emergency help in Jonesboro, GA?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our dispatch line is open 24 hours a day, 7 days a week for immediate assistance.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild size="lg" className="font-semibold shadow-sm">
              <Link to="/contact/" className="inline-flex items-center gap-2">
                <PhoneCall className="size-4" />
                <span>Contact Dispatch</span>
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="font-semibold">
              <Link to="/problems/">
                <span>View All Problems</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}